import React from "react";
import { useTranslation } from "react-i18next";
import ProgressPage from "./ProgressPage";
import { ProgressCallback } from "../types/ProgressTypes";
import { CarboWasteEndpoints_UpdateCompany } from "../../api/endpoints";
import { config } from "../../config/environment";

interface UpdateCompanyPageProps {
    companyId: string;
    deviceFingerprint: string;
    onComplete: () => void;
}

const UpdateCompanyPage: React.FC<UpdateCompanyPageProps> = ({
    companyId,
    deviceFingerprint,
    onComplete,
}) => {
    const { t } = useTranslation();

    const updateCompanyData = async (callbacks: ProgressCallback) => {
        try {
            callbacks.onProgress(
                t("update_company.progress.updating", "Aktualizowanie danych firmy..."),
                0
            );

            const payload = {
                deviceFingerprint,
                companyId,
            };

            console.log('📤 Updating company:', payload);

            const response = await fetch(CarboWasteEndpoints_UpdateCompany, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            callbacks.onProgress(
                t("update_company.progress.processing", "Przetwarzanie odpowiedzi..."),
                50
            );

            // Check content type
            const contentType = response.headers.get("content-type");
            const isJson = contentType?.includes("application/json");

            if (!response.ok) {
                // Handle error response
                if (isJson) {
                    const errorData = await response.json();
                    const errorMessage = errorData.details || errorData.error || `HTTP ${response.status}`;
                    callbacks.onError(
                        t("update_company.error.failed", `Błąd aktualizacji: ${errorMessage}`)
                    );
                } else {
                    const errorText = await response.text();
                    callbacks.onError(
                        t("update_company.error.failed", `Błąd aktualizacji: ${errorText || response.statusText}`)
                    );
                }
                return;
            }

            // Success - parse DeviceSubscription
            if (!isJson) {
                callbacks.onError(
                    t("update_company.error.invalid_response", "Nieprawidłowa odpowiedź serwera")
                );
                return;
            }

            const subscriptionData = await response.json();

            callbacks.onProgress(
                t("update_company.progress.complete", "Dane firmy zaktualizowane"),
                100
            );

            console.log('✅ Company updated successfully:', subscriptionData);

            callbacks.onSuccess(
                t("update_company.success", "Firma została pomyślnie zaktualizowana")
            );

        } catch (error) {
            console.error('❌ Update company error:', error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            callbacks.onError(
                t("update_company.error.network", `Błąd połączenia: ${errorMessage}`)
            );
        }
    };

    return (
        <ProgressPage
            fetchData={updateCompanyData}
            loadingMessage={t("update_company.loading", "Aktualizowanie danych firmy...")}
            successMessage={t("update_company.success", "Firma została pomyślnie zaktualizowana")}
            backTo="bdo-entities"
            autoReturnDelay={2000}
            onComplete={onComplete}
        />
    );
};

export default UpdateCompanyPage;
